import { useEffect, useRef } from "react";

import {
  claimWebModalReturnFocusTarget,
  restoreWebModalReturnFocusTarget,
} from "./webModalReturnFocus";

/**
 * Class on <html> that pins the page. The declarations live in a stylesheet
 * rather than inline on <body> so the lock never has to snapshot and re-apply
 * the page's own styles — the only state it keeps is the scroll offset, which
 * it needs regardless.
 */
const SCROLL_LOCK_CLASS = "jsma-modal-scroll-locked";
/** Companion class applied only when the page scrollbar consumes layout space. */
const SCROLL_LOCK_GUTTER_CLASS = "jsma-modal-scroll-locked-gutter";
const SCROLL_LOCK_X_PROPERTY = "--jsma-modal-scroll-lock-x";
const SCROLL_LOCK_Y_PROPERTY = "--jsma-modal-scroll-lock-y";
const SCROLL_LOCK_STYLE_ID = "jsma-modal-scroll-lock";

/*
 * `html.<class> > body` is specificity (0,1,1), so it beats any `body { … }`
 * rule the host page ships. A host that sets these inline on <body> would
 * still win; no host in this repo does.
 */
const SCROLL_LOCK_CSS = `
.${SCROLL_LOCK_CLASS} > body {
  position: fixed;
  top: calc(-1 * var(${SCROLL_LOCK_Y_PROPERTY}, 0px));
  left: calc(-1 * var(${SCROLL_LOCK_X_PROPERTY}, 0px));
  right: var(${SCROLL_LOCK_X_PROPERTY}, 0px);
}
.${SCROLL_LOCK_GUTTER_CLASS} {
  overflow-y: scroll;
}
`;

let scrollLockCount = 0;
let savedScroll = { x: 0, y: 0 };
let savedScrollRestoration: ScrollRestoration | undefined;
/** Whether the declarations below are currently applied, as opposed to merely counted. */
let isLocked = false;

function ensureScrollLockStylesheet() {
  if (document.getElementById?.(SCROLL_LOCK_STYLE_ID)) return;
  const style = document.createElement?.("style");
  if (!style) return;
  style.id = SCROLL_LOCK_STYLE_ID;
  style.textContent = SCROLL_LOCK_CSS;
  document.head?.appendChild?.(style);
}

function readPageScroll() {
  if (typeof window === "undefined") return { x: 0, y: 0 };
  return {
    x: typeof window.scrollX === "number" ? window.scrollX : 0,
    y: typeof window.scrollY === "number" ? window.scrollY : 0,
  };
}

/**
 * True when the page scrollbar takes layout space (Windows/Linux, or macOS set
 * to always show scroll bars) rather than floating over the content.
 */
function hasSpaceConsumingScrollbar() {
  if (typeof window === "undefined") return false;
  const viewportWidth = window.innerWidth;
  const contentWidth = document.documentElement?.clientWidth;
  if (typeof viewportWidth !== "number" || typeof contentWidth !== "number") return false;
  return viewportWidth > contentWidth;
}

/**
 * Takes ownership of scroll restoration for the duration of the lock, and
 * reports what it was.
 *
 * Pinning <body> collapses the document, so anything reading the page offset
 * while a modal is open reads 0 — including the browser, which records it
 * against the history entry a route-pushing modal leaves behind, then restores
 * that 0 over our own restore when the modal is dismissed.
 */
function takeManualScrollRestoration(): ScrollRestoration | undefined {
  if (typeof history === "undefined" || !("scrollRestoration" in history)) return undefined;
  const previous = history.scrollRestoration;
  history.scrollRestoration = "manual";
  return previous;
}

function giveBackScrollRestoration(previous: ScrollRestoration | undefined) {
  if (previous === undefined || typeof history === "undefined") return;
  history.scrollRestoration = previous;
}

/**
 * Freezes the page behind a modal by pinning <body> at the current scroll
 * offset. Do not switch this back to `overflow: hidden`: root `overflow`
 * propagates to the viewport, which then stops being a scrollport, and every
 * `position: sticky` descendant collapses to its static position.
 *
 * Pinning keeps the rendered geometry identical to the scrolled page, so
 * sticky resolves to the same place. It also freezes either page-level
 * scroller — in brownfield hosts the page scrolls the document element, not
 * <body>.
 *
 * Pinning empties the document, which would drop the page scrollbar and widen
 * the viewport by its width; anything sized to the page then falls short of
 * the viewport, leaving a strip of bare canvas beside a modal backdrop. The
 * gutter class holds the scrollbar open so no width changes. `scroll` keeps
 * the viewport a scrollport; only `hidden` breaks sticky.
 */
function acquireScrollLock() {
  if (scrollLockCount === 0) {
    const html = document.documentElement;
    if (html?.classList) {
      ensureScrollLockStylesheet();
      savedScroll = readPageScroll();
      html.style?.setProperty?.(SCROLL_LOCK_X_PROPERTY, `${savedScroll.x}px`);
      html.style?.setProperty?.(SCROLL_LOCK_Y_PROPERTY, `${savedScroll.y}px`);
      // Measured before the class lands, while the scrollbar is still there.
      if (hasSpaceConsumingScrollbar()) {
        html.classList.add(SCROLL_LOCK_GUTTER_CLASS);
      }
      html.classList.add(SCROLL_LOCK_CLASS);
      savedScrollRestoration = takeManualScrollRestoration();
      isLocked = true;
    }
  }
  // Counted even when there was nothing to lock, so that every acquire is
  // balanced by its own release. Skipping the increment would let one trap's
  // cleanup decrement a count a *different* trap owns, unpinning the page
  // while that one's modal is still open.
  scrollLockCount++;
}

function releaseScrollLock() {
  if (scrollLockCount === 0) return;
  scrollLockCount--;
  if (scrollLockCount > 0 || !isLocked) return;
  isLocked = false;

  const html = document.documentElement;
  html?.classList?.remove(SCROLL_LOCK_CLASS, SCROLL_LOCK_GUTTER_CLASS);
  html?.style?.removeProperty?.(SCROLL_LOCK_X_PROPERTY);
  html?.style?.removeProperty?.(SCROLL_LOCK_Y_PROPERTY);
  // Must follow the class removal: until <body> is back in flow there is
  // nothing to scroll.
  if (typeof window !== "undefined") {
    window.scrollTo?.(savedScroll.x, savedScroll.y);
  }
  giveBackScrollRestoration(savedScrollRestoration);
  savedScrollRestoration = undefined;
}

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

function isFocusWithinControlledPortal(dialogEl: HTMLElement, target: EventTarget | null) {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  const targetElement = target as HTMLElement | null;
  const portal = targetElement?.closest?.<HTMLElement>("[id]");
  if (!portal?.id) return false;

  const escapedId = portal.id.replaceAll("\\", "\\\\").replaceAll('"', '\\"');
  return dialogEl.querySelector?.(`[aria-controls="${escapedId}"]`) != null;
}

/**
 * Nested portaled dialogs (e.g. a confirm Dialog opened above a full-screen
 * drawer) live outside the parent trap's DOM subtree. Allow focus there so the
 * parent trap does not immediately steal it back.
 */
function isFocusWithinNestedModal(dialogEl: HTMLElement, target: EventTarget | null) {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  const targetElement = target as HTMLElement | null;
  const nestedDialog = targetElement?.closest?.<HTMLElement>('[role="dialog"][aria-modal="true"]');
  if (
    !nestedDialog ||
    nestedDialog === dialogEl ||
    nestedDialog.getAttribute?.("role") !== "dialog" ||
    nestedDialog.getAttribute?.("aria-modal") !== "true"
  ) {
    return false;
  }
  return !dialogEl.contains(nestedDialog);
}

/**
 * Clears `inert` on this dialog's body-level portal in case a parent
 * modal's focus trap inerted it. Mirrors MADS `Dialog.web.tsx` (RN-4068).
 */
export function uninertOwnPortal(dialogEl: HTMLElement | null) {
  if (!dialogEl || typeof document === "undefined") return;
  let bodyChild = dialogEl;
  while (bodyChild.parentElement && bodyChild.parentElement !== document.body) {
    bodyChild = bodyChild.parentElement;
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  if ((bodyChild as HTMLElement & { inert?: boolean }).inert) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    (bodyChild as HTMLElement & { inert?: boolean }).inert = false;
  }
}

/**
 * Applies dialog isolation to `dialogEl` and returns a cleanup function.
 *
 * Shared by {@link useModalFocusTrap} (self-contained modals whose dialog
 * element is passed by ref) and {@link useSceneModalFocusTrap} (React
 * Navigation screens whose dialog element is resolved from the scene). When
 * `applyDialogRole` is true, `role="dialog"`/`aria-modal` are set on the
 * element for the duration of the trap and restored on cleanup — used for the
 * navigation case, where the dialog element is a container owned by the
 * navigator and can't receive those props declaratively.
 */
function activateModalFocusTrap(
  dialogEl: HTMLElement | null,
  previousFocusRef: React.RefObject<Element | null>,
  applyDialogRole: boolean,
): () => void {
  previousFocusRef.current = document.activeElement;
  const returnFocusTarget = claimWebModalReturnFocusTarget();

  uninertOwnPortal(dialogEl);

  acquireScrollLock();

  let restoreDialogRole: (() => void) | undefined;
  if (applyDialogRole && dialogEl) {
    const prevRole = dialogEl.getAttribute("role");
    const prevAriaModal = dialogEl.getAttribute("aria-modal");
    dialogEl.setAttribute("role", "dialog");
    dialogEl.setAttribute("aria-modal", "true");
    restoreDialogRole = () => {
      if (prevRole === null) dialogEl.removeAttribute("role");
      else dialogEl.setAttribute("role", prevRole);
      if (prevAriaModal === null) dialogEl.removeAttribute("aria-modal");
      else dialogEl.setAttribute("aria-modal", prevAriaModal);
    };
  }

  // Move focus into the modal. Prefer the heading; fall back to the dialog
  // container. Either target may be a non-focusable element (a heading, or a
  // navigator-owned scene <div> with no tabindex), so make it programmatically
  // focusable for the duration of the trap and restore its state on cleanup —
  // otherwise `focus()` is a no-op and focus stays outside the modal.
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  const heading = dialogEl?.querySelector?.('[role="heading"]') as HTMLElement | null;
  const focusTarget = heading ?? dialogEl;
  let restoreFocusTargetTabIndex: (() => void) | undefined;
  if (focusTarget) {
    if (focusTarget.tabIndex < 0 && !focusTarget.hasAttribute?.("tabindex")) {
      focusTarget.setAttribute("tabindex", "-1");
      restoreFocusTargetTabIndex = () => focusTarget.removeAttribute("tabindex");
    }
    focusTarget.focus?.();
  }

  const handleFocusIn = (e: FocusEvent) => {
    if (
      dialogEl &&
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      !dialogEl.contains(e.target as Node) &&
      !isFocusWithinControlledPortal(dialogEl, e.target) &&
      !isFocusWithinNestedModal(dialogEl, e.target)
    ) {
      e.stopPropagation();
      focusTarget?.focus?.();
    }
  };
  document.addEventListener("focusin", handleFocusIn);

  // `focusin` cannot catch focus escaping to browser chrome (URL bar).
  // Intercept Tab/Shift+Tab to cycle within the modal instead.
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== "Tab" || e.altKey || e.ctrlKey || e.metaKey || !dialogEl) return;
    const focusable = Array.from(dialogEl.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => !el.closest("[inert]"),
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };
  document.addEventListener("keydown", handleKeyDown);

  const inertedElements: HTMLElement[] = [];
  let node: HTMLElement | null = dialogEl?.parentElement ?? null;
  while (node && node !== document.body) {
    const parent: HTMLElement | null = node.parentElement;
    if (parent) {
      for (const sibling of Array.from(parent.children)) {
        if (sibling === node) continue;
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        if (!(sibling as HTMLElement).inert) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
          (sibling as HTMLElement).inert = true;
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
          inertedElements.push(sibling as HTMLElement);
        }
      }
    }
    node = parent;
  }

  return () => {
    releaseScrollLock();
    document.removeEventListener("focusin", handleFocusIn);
    document.removeEventListener("keydown", handleKeyDown);
    for (const el of inertedElements) {
      el.inert = false;
    }
    restoreFocusTargetTabIndex?.();
    restoreDialogRole?.();
    if (returnFocusTarget) {
      restoreWebModalReturnFocusTarget(returnFocusTarget);
    } else {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      const prev = previousFocusRef.current as HTMLElement | null;
      if (prev && document.contains(prev)) {
        prev.focus?.();
      }
    }
    previousFocusRef.current = null;
  };
}

/**
 * Supplements React Native's Modal on web.
 *
 * On native, RNModal presents a separate OS-level screen that automatically
 * isolates focus, hides background content from assistive technology, and
 * restores focus on dismiss. On web the modal is a portal-rendered <div>,
 * so the browser has no built-in awareness of it. This hook fills that gap:
 *
 * 1. Moves focus into the modal heading (or the modal container) on open
 * 2. Traps keyboard focus so Tab cannot reach elements behind the backdrop
 * 3. Marks all DOM siblings as `inert` to hide them from screen readers
 * 4. Restores focus to the registered trigger element on close, or falls back
 *    to the previously-focused element
 */
export function useModalFocusTrap(dialogRef: React.RefObject<object | null>, visible: boolean) {
  const previousFocusRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!visible) return;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    const dialogEl = dialogRef.current as unknown as HTMLElement | null;
    return activateModalFocusTrap(dialogEl, previousFocusRef, false);
  }, [visible, dialogRef]);
}

/**
 * Resolves the React Navigation scene container that wraps a screen's body.
 *
 * A stack scene renders as `sceneContainer > [header, sceneView > screenBody]`
 * (see `@react-navigation/stack` CardContainer). Given a ref to the screen body,
 * the container is two element levels up. The trap must operate on that
 * container — not the body — so the separately-slotted header (the close/back
 * button) stays inside the trap instead of being inerted as a sibling. Falls
 * back to the body element if the expected ancestors aren't present.
 */
function resolveSceneContainer(bodyEl: HTMLElement | null): HTMLElement | null {
  if (!bodyEl) return null;
  const sceneView = bodyEl.parentElement;
  const sceneContainer = sceneView?.parentElement ?? null;
  return sceneContainer ?? bodyEl;
}

/**
 * Focus trap for a modal presented as a React Navigation stack screen (e.g. a
 * `transparentModal`), where the header and body are rendered in separate
 * navigator slots rather than inside one dialog element.
 *
 * Unlike {@link useModalFocusTrap}, which takes a ref to the dialog element
 * directly, this takes a ref to the screen body and resolves the enclosing
 * scene container (header + body) as the dialog. The container is then given
 * `role="dialog"`/`aria-modal` for the duration of the trap.
 */
export function useSceneModalFocusTrap(bodyRef: React.RefObject<object | null>, visible: boolean) {
  const previousFocusRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!visible) return;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    const bodyEl = bodyRef.current as unknown as HTMLElement | null;
    const dialogEl = resolveSceneContainer(bodyEl);
    return activateModalFocusTrap(dialogEl, previousFocusRef, true);
  }, [visible, bodyRef]);
}

/** Closes the modal when the user presses Escape. */
export function useEscapeKey(onCloseRef: React.RefObject<(() => void) | null>, visible: boolean) {
  useEffect(() => {
    if (!visible) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current?.();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [visible, onCloseRef]);
}
